/**
 * WorkspaceSetupModal
 * ─────────────────────────────────────────────────────────────────────────────
 * One-click workspace setup wizard. Pick a vertical, name your workspace, and
 * Zentrix spins up RBAC roles, workflow automations, invoice settings and an
 * example project automatically. Under 2 minutes, fully configured.
 */
import { useState, useEffect, useRef } from 'react';
import { useCreateWorkspaceMutation, useGetVerticalCatalogueQuery } from '@api/workspaceApi';
import toast from 'react-hot-toast';

// ── Vertical metadata (icons, gradient, tagline) ───────────────────────────
const VERTICAL_META = {
  software: {
    emoji: '',
    tagline: 'Sprints, bugs, deploys & retrospectives',
    gradient: 'from-blue-600 to-cyan-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    ring: 'ring-blue-400',
    text: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-700',
  },
  marketing: {
    emoji: '',
    tagline: 'Briefs, campaigns, creatives & clients',
    gradient: 'from-rose-500 to-pink-400',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    ring: 'ring-rose-400',
    text: 'text-rose-700',
    badge: 'bg-rose-100 text-rose-700',
  },
  legal: {
    emoji: '⚖️',
    tagline: 'Matters, billable time & client portal',
    gradient: 'from-slate-700 to-slate-500',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    ring: 'ring-slate-400',
    text: 'text-slate-700',
    badge: 'bg-slate-100 text-slate-700',
  },
  construction: {
    emoji: '',
    tagline: 'Sites, phases, QS & subcontractors',
    gradient: 'from-amber-600 to-yellow-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    ring: 'ring-amber-400',
    text: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-700',
  },
  event_planning: {
    emoji: '',
    tagline: 'Venues, vendors, logistics & go-live',
    gradient: 'from-brand-700 to-brand-500',
    bg: 'bg-brand-50',
    border: 'border-brand-200',
    ring: 'ring-brand-500',
    text: 'text-brand-700',
    badge: 'bg-brand-gradient text-brand-700',
  },
  product_launch: {
    emoji: '',
    tagline: 'Roadmap, GTM, cross-functional launch',
    gradient: 'from-sky-600 to-blue-400',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    ring: 'ring-sky-400',
    text: 'text-sky-700',
    badge: 'bg-sky-100 text-sky-700',
  },
  itsm: {
    emoji: '',
    tagline: 'Incidents, changes, SLAs & ITIL',
    gradient: 'from-indigo-600 to-blue-500',
    bg: 'bg-brand-50',
    border: 'border-indigo-200',
    ring: 'ring-indigo-400',
    text: 'text-brand-700',
    badge: 'bg-brand-gradient text-brand-700',
  },
  finance: {
    emoji: '',
    tagline: 'Budgets, compliance, audits & reporting',
    gradient: 'from-emerald-700 to-green-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    ring: 'ring-emerald-400',
    text: 'text-emerald-700',
    badge: 'bg-emerald-100 text-emerald-700',
  },
  retail: {
    emoji: '🛍️',
    tagline: 'Inventory, campaigns & seasonal launches',
    gradient: 'from-pink-600 to-rose-400',
    bg: 'bg-pink-50',
    border: 'border-pink-200',
    ring: 'ring-pink-400',
    text: 'text-pink-700',
    badge: 'bg-pink-100 text-pink-700',
  },
  generic: {
    emoji: '',
    tagline: 'Flexible setup for any team',
    gradient: 'from-gray-600 to-gray-400',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    ring: 'ring-gray-400',
    text: 'text-gray-700',
    badge: 'bg-gray-100 text-gray-700',
  },
};

// ── What we set up for each vertical (shown in the preview panel) ──────────
const SETUP_CHECKLIST = [
  { icon: '', label: 'Board columns & task statuses' },
  { icon: '', label: 'Industry-specific RBAC roles' },
  { icon: '', label: 'Workflow automations' },
  { icon: '', label: 'Invoice settings & templates' },
  { icon: '', label: 'Example project with tasks' },
];

const DISPLAY_ORDER = [
  'software', 'marketing', 'legal', 'construction',
  'event_planning', 'product_launch', 'itsm', 'finance', 'retail', 'generic',
];

// ── Animated counter ──────────────────────────────────────────────────────
const Counter = ({ n }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!n) return;
    let start = 0;
    const step = Math.ceil(n / 12);
    const id = setInterval(() => {
      start = Math.min(start + step, n);
      setDisplay(start);
      if (start >= n) clearInterval(id);
    }, 40);
    return () => clearInterval(id);
  }, [n]);
  return <>{display}</>;
};

// ── Step 1: vertical picker ────────────────────────────────────────────────
function VerticalPicker({ selected, onSelect, catalogue }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5">
      {DISPLAY_ORDER.map((key) => {
        const meta = VERTICAL_META[key] || VERTICAL_META.generic;
        const cat  = catalogue?.find(c => c.industry === key);
        const active = selected === key;
        return (
          <button
            key={key}
            onClick={() => onSelect(key)}
            className={`
              relative flex flex-col items-center gap-2 p-3.5 rounded-2xl border-2 text-center
              transition-all duration-150 cursor-pointer select-none
              ${active
                ? `${meta.border} ${meta.ring} ring-2 ${meta.bg} shadow-md scale-[1.03]`
                : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm'
              }
            `}
          >
            {active && (
              <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 fill-white"><path d="M1.5 5.5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
              </span>
            )}
            <span className="text-2xl leading-none">{meta.emoji}</span>
            <div>
              <p className={`text-[11px] font-black uppercase tracking-wide leading-tight ${active ? meta.text : 'text-slate-700'}`}>
                {key.replace(/_/g, ' ')}
              </p>
              {cat && (
                <p className="text-[8px] text-slate-500 mt-0.5 leading-tight">{meta.tagline}</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── Step 2: workspace name ─────────────────────────────────────────────────
function NameStep({ industry, name, setName, nameRef }) {
  const meta = VERTICAL_META[industry] || VERTICAL_META.generic;
  const placeholders = {
    software:      'Acme Engineering',
    marketing:     'Pixel Creative Agency',
    legal:         'Morrison & Partners',
    construction:  'BuildCo Projects',
    event_planning:'Evently Events',
    product_launch:'LaunchPad HQ',
    itsm:          'IT Operations',
    finance:       'Meridian Capital',
    retail:        'Storefront HQ',
    generic:       'My Workspace',
  };
  return (
    <div className="space-y-5">
      {/* Vertical badge */}
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold ${meta.badge}`}>
        <span>{meta.emoji}</span>
        <span className="capitalize">{industry.replace(/_/g, ' ')} workspace</span>
      </div>

      {/* Name input */}
      <div>
        <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider mb-2">
          Workspace Name
        </label>
        <input
          ref={nameRef}
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={placeholders[industry] || 'My Workspace'}
          className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-[15px] font-semibold text-slate-800 placeholder-slate-300 focus:outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-50 transition-all"
          autoFocus
          maxLength={80}
        />
        <p className="text-[11px] text-slate-500 mt-1.5">You can always change this later in workspace settings.</p>
      </div>

      {/* What gets set up */}
      <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
        <p className="text-[11px] font-black text-slate-500 uppercase tracking-wider mb-3">
          What Zentrix will set up for you
        </p>
        <div className="space-y-2">
          {SETUP_CHECKLIST.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <span className="text-base leading-none">{item.icon}</span>
              <span className="text-[12px] text-slate-600 font-medium">{item.label}</span>
              <svg viewBox="0 0 10 10" className="w-3 h-3 ml-auto text-green-500 flex-shrink-0">
                <path d="M1.5 5.5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
              </svg>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: preview panel ──────────────────────────────────────────────────
function PreviewPanel({ industry, catalogue }) {
  const meta = VERTICAL_META[industry] || VERTICAL_META.generic;
  const cat  = catalogue?.find(c => c.industry === industry);

  if (!cat) return null;

  return (
    <div className="space-y-4">
      {/* Gradient hero */}
      <div className={`bg-gradient-to-br ${meta.gradient} rounded-2xl p-4 text-white`}>
        <div className="flex items-center gap-3 mb-1">
          <span className="text-3xl">{meta.emoji}</span>
          <div>
            <p className="text-[13px] font-black capitalize">{industry.replace(/_/g, ' ')}</p>
            <p className="text-[11px] opacity-80">{meta.tagline}</p>
          </div>
        </div>
        <div className="flex gap-3 mt-3">
          {[
            { n: cat.roleCount,     l: 'Roles' },
            { n: cat.workflowCount, l: 'Automations' },
            { n: 1,                 l: 'Example Project' },
          ].map((s, i) => (
            <div key={i} className="bg-white/15 rounded-xl px-3 py-1.5 text-center">
              <div className="text-[18px] font-black"><Counter n={s.n} /></div>
              <div className="text-[8px] opacity-80 uppercase tracking-wide">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Roles list */}
      {cat.roles?.length > 0 && (
        <div>
          <p className="text-[11px] font-black text-slate-500 uppercase tracking-wider mb-2">Roles included</p>
          <div className="space-y-1.5">
            {cat.roles.map((r, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-white rounded-xl border border-slate-100">
                <div className="w-5 h-5 rounded-full flex-shrink-0" style={{ background: r.color }} />
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-slate-700 truncate">{r.name}</p>
                  <p className="text-[8px] text-slate-500 truncate">{r.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Automations list */}
      {cat.workflows?.length > 0 && (
        <div>
          <p className="text-[11px] font-black text-slate-500 uppercase tracking-wider mb-2">Automations installed</p>
          <div className="space-y-1.5">
            {cat.workflows.map((w, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-white rounded-xl border border-slate-100">
                <span className="text-xs mt-0.5 flex-shrink-0"></span>
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-slate-700">{w.name}</p>
                  <p className="text-[8px] text-slate-500">{w.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Example project */}
      {cat.exampleProjectName && (
        <div>
          <p className="text-[11px] font-black text-slate-500 uppercase tracking-wider mb-2">Example project</p>
          <div className={`flex items-center gap-2 p-2.5 rounded-xl border ${meta.border} ${meta.bg}`}>
            <span className="text-base"></span>
            <p className={`text-[11px] font-bold ${meta.text}`}>{cat.exampleProjectName}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Creating animation ─────────────────────────────────────────────────────
function CreatingScreen({ wsName, industry }) {
  const meta = VERTICAL_META[industry] || VERTICAL_META.generic;
  const steps = [
    ' Applying industry template…',
    ' Creating RBAC roles…',
    ' Installing workflow automations…',
    ' Configuring invoice settings…',
    ' Generating example project…',
    ` "${wsName}" is ready!`,
  ];
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step >= steps.length - 1) return;
    const t = setTimeout(() => setStep(s => s + 1), 600);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center mb-5 shadow-lg`}>
        <span className="text-3xl">{meta.emoji}</span>
      </div>
      <h3 className="text-[16px] font-black text-slate-800 mb-1">Setting up your workspace</h3>
      <p className="text-[11px] text-slate-500 mb-6">This takes just a moment…</p>

      <div className="w-full max-w-xs space-y-2 text-left">
        {steps.map((s, i) => (
          <div
            key={i}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-[11px] font-medium transition-all duration-300
              ${i < step  ? 'text-slate-500 opacity-50' : ''}
              ${i === step ? 'bg-slate-50 text-slate-800 shadow-sm scale-[1.02]' : ''}
              ${i > step  ? 'text-slate-500' : ''}
            `}
          >
            {i < step && (
              <span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 10 10" className="w-2.5 h-2.5"><path d="M1.5 5.5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/></svg>
              </span>
            )}
            {i === step && (
              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                <span className="w-3 h-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin block" />
              </span>
            )}
            {i > step && (
              <span className="w-4 h-4 rounded-full border-2 border-slate-200 flex-shrink-0" />
            )}
            {s}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main modal component ──────────────────────────────────────────────────
export default function WorkspaceSetupModal({ open, onClose }) {
  const [step, setStep]           = useState(1); // 1=pick, 2=name, 3=creating
  const [industry, setIndustry]   = useState('software');
  const [wsName, setWsName]       = useState('');
  const nameRef = useRef(null);

  const { data: catRes } = useGetVerticalCatalogueQuery(undefined, { skip: !open });
  const catalogue = catRes?.data || [];

  const [createWorkspace, { isLoading }] = useCreateWorkspaceMutation();

  // Reset when opened
  useEffect(() => {
    if (open) { setStep(1); setIndustry('software'); setWsName(''); }
  }, [open]);

  // Focus name input when step 2 opens
  useEffect(() => {
    if (step === 2) setTimeout(() => nameRef.current?.focus(), 80);
  }, [step]);

  const handleCreate = async () => {
    if (!wsName.trim()) { toast.error('Please enter a workspace name'); return; }
    setStep(3);
    try {
      const res = await createWorkspace({ name: wsName.trim(), industry }).unwrap();
      // Let the animation play out at least 3.6 s
      await new Promise(r => setTimeout(r, 3700));
      const wsId = res?.data?.id || res?.data?._id;
      if (wsId) localStorage.setItem('activeWorkspaceId', wsId);
      toast.success(`${wsName} is ready!`);
      onClose();
      window.location.reload();
    } catch (err) {
      setStep(2);
      toast.error(err?.data?.error?.message || 'Failed to create workspace');
    }
  };

  if (!open) return null;

  const meta = VERTICAL_META[industry] || VERTICAL_META.generic;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget && step !== 3) onClose(); }}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full overflow-hidden flex flex-col"
        style={{ maxWidth: step === 1 ? 720 : step === 2 ? 900 : 480, maxHeight: '90vh' }}>

        {/* ── Header ── */}
        {step !== 3 && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Zentrix brand mark */}
              <div className="w-8 h-8 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#2a2a72,#1338be)' }}>
                <svg viewBox="0 0 16 16" className="w-4 h-4 fill-white">
                  <path d="M2 2h5l7 12H9L2 2zM7 2h7v5L7 2zM2 14l5-5v5H2z"/>
                </svg>
              </div>
              <div>
                <p className="text-[13px] font-black text-slate-800">
                  {step === 1 ? 'Choose your vertical' : 'Name your workspace'}
                </p>
                <p className="text-[11px] text-slate-500">
                  Step {step} of 2 — Zentrix One-Click Setup
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Step dots */}
              <div className="flex gap-1">
                {[1, 2].map(s => (
                  <div key={s}
                    className={`h-1.5 rounded-full transition-all ${s === step ? 'w-6 bg-blue-500' : s < step ? 'w-2 bg-green-400' : 'w-2 bg-slate-200'}`}
                  />
                ))}
              </div>
              <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors">
                <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 fill-current"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/></svg>
              </button>
            </div>
          </div>
        )}

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto">

          {/* Step 1: Vertical picker */}
          {step === 1 && (
            <div className="p-6 space-y-4">
              <div>
                <h2 className="text-[22px] font-black text-slate-900 leading-tight">
                  What kind of team are you?
                </h2>
                <p className="text-[13px] text-slate-500 mt-1">
                  Zentrix will configure everything for your industry — roles, workflows, boards and more.
                </p>
              </div>
              <VerticalPicker
                selected={industry}
                onSelect={setIndustry}
                catalogue={catalogue}
              />
            </div>
          )}

          {/* Step 2: Name + preview side-by-side */}
          {step === 2 && (
            <div className="flex min-h-0">
              {/* Left: name form */}
              <div className="flex-1 p-6 border-r border-slate-100 min-w-0">
                <NameStep
                  industry={industry}
                  name={wsName}
                  setName={setWsName}
                  nameRef={nameRef}
                />
              </div>
              {/* Right: preview */}
              <div className="w-72 p-5 bg-slate-50/70 overflow-y-auto flex-shrink-0">
                <PreviewPanel industry={industry} catalogue={catalogue} />
              </div>
            </div>
          )}

          {/* Step 3: creating animation */}
          {step === 3 && (
            <CreatingScreen wsName={wsName} industry={industry} />
          )}
        </div>

        {/* ── Footer ── */}
        {step !== 3 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex-shrink-0">
            <button
              onClick={() => step === 1 ? onClose() : setStep(1)}
              className="text-[12px] font-semibold text-slate-500 hover:text-slate-700 transition-colors"
            >
              {step === 1 ? 'Cancel' : '← Back'}
            </button>

            {step === 1 && (
              <button
                onClick={() => setStep(2)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[12px] font-black text-white shadow-md hover:shadow-lg transition-all active:scale-95"
                style={{ background: 'linear-gradient(135deg,#2a2a72,#1338be)' }}
              >
                Continue →
              </button>
            )}

            {step === 2 && (
              <button
                onClick={handleCreate}
                disabled={!wsName.trim() || isLoading}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[12px] font-black text-white shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r ${meta.gradient}`}
              >
                <span>{meta.emoji}</span>
                Launch workspace
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
